import * as React from 'react'
import { TextField } from '../ui/TextField'
import { Button, ButtonText } from '../ui/button'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import useAppStore from '@/store/useAppStore'
import NonModalWindow from '@/components/NonModalWindow'
import useEditTransactionCategoryGroup from '@/services/api/transaction-category/useEditTransactionCategoryGroup'
import useEditVaultGroup from '@/services/api/vault/useEditVaultGroup'
import { useNavigate } from 'react-router-dom'
import CloseConfirmationModal from '../CloseConfirmationModal'

export interface GroupValues {
  name: string
  description?: string
}

export default function EditGroupForm() {
  const navigate = useNavigate()
  const editedEntity = useAppStore((state) => state.editedEntity)
  const [isFocusedName, setIsFocusedName] = useState(false)
  const [isFocusedDescription, setIsFocusedNameDescription] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
  } = useForm<GroupValues>({
    defaultValues: {},
  })
  const toggleNonModalWindow = useAppStore(
    (state) => state.toggleNonModalWindowState,
  )

  const { editTransactionCategoryGroup } = useEditTransactionCategoryGroup(
    editedEntity.id,
  )
  const { editVaultGroup } = useEditVaultGroup(editedEntity.id)

  const [name, setName] = useState(editedEntity.name)
  const [description, setDescription] = useState(editedEntity.description)

  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  React.useEffect(() => {
    setValue('name', name)
  }, [name, editedEntity])

  const handleNameChange = (event) => {
    const value = event.target.value
    if (value.length <= 30) {
      setName(value)
    }
  }

  React.useEffect(() => {
    setValue('description', description)
  }, [description, editedEntity])

  const handleDescriptionChange = (event) => {
    const value = event.target.value
    if (value.length <= 40) {
      setDescription(value)
    }
  }

  const onSubmit = async (data) => {
    if (editedEntity.entity_type === 'vault_group') {
      await editVaultGroup({ ...data })
    } else if (editedEntity.entity_type === 'transaction_category_group') {
      await editTransactionCategoryGroup({ ...data })
    }
    navigate('/')
    toggleNonModalWindow()
  }

  return (
    <>
      <CloseConfirmationModal
        open={showConfirmationModal}
        setOpen={setShowConfirmationModal}
        onConfirm={() => {
          setShowConfirmationModal(false)
          toggleNonModalWindow()
          navigate('/')
        }}
      />

      <form
        noValidate
        className=" flex flex-col"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const activeElement = document.activeElement as HTMLElement
            if (
              activeElement.tagName === 'INPUT' ||
              activeElement.tagName === 'TEXTAREA'
            ) {
              e.preventDefault()
            }
          }
        }}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit(onSubmit)
        }}
      >
        <NonModalWindow
          onSubmit={handleSubmit(onSubmit)}
          title={'Edit group '}
          // onClose={() => {
          //     toggleNonModalWindow()
          // }}
          onClose={() => {
            setShowConfirmationModal(isDirty)
            // setNonModalWindowState('closed')
          }}
          isDirty
        >
          <NonModalWindow.Body className="min-h-[78vh]">
            <div className="mb-3">
              <TextField
                status={errors?.name ? 'error' : null}
                statusText={errors.name?.message}
              >
                <TextField.Label className="flex justify-between text-sm">
                  <span>Group Name</span>
                  <span
                    className={`text-txt-secondary ${isFocusedName ? '' : 'hidden'}`}
                  >
                    {name?.length}/30
                  </span>
                </TextField.Label>
                <TextField.Input
                  maxLength={30}
                  placeholder="e.g., Revenue"
                  {...register('name', {
                    required: 'Please fill out this field',
                    value: name,
                    onChange: handleNameChange,
                  })}
                  onFocus={() => setIsFocusedName(true)}
                  onBlur={() => setIsFocusedName(false)}
                />
              </TextField>
            </div>
            <div className="mb-4">
              <TextField>
                <TextField.Label className="flex justify-between text-sm">
                  Group Description
                  <span
                    className={`text-txt-secondary ${isFocusedDescription ? '' : 'hidden'}`}
                  >
                    {description?.length}/40
                  </span>
                </TextField.Label>
                <TextField.Input
                  maxLength={40}
                  placeholder="e.g., For main company accounts"
                  {...register('description', {
                    value: description,
                    onChange: handleDescriptionChange,
                  })}
                  onFocus={() => setIsFocusedNameDescription(true)}
                  onBlur={() => setIsFocusedNameDescription(false)}
                />
              </TextField>
            </div>
          </NonModalWindow.Body>
          <NonModalWindow.Footer>
            <Button
              variant="secondary"
              className="mr-2"
              onClick={() => {
                setShowConfirmationModal(true)
              }}
            >
              <ButtonText>Cancel</ButtonText>
            </Button>
            <Button type="submit" onClick={handleSubmit(onSubmit)}>
              <ButtonText>Save</ButtonText>
            </Button>
          </NonModalWindow.Footer>
        </NonModalWindow>
      </form>
    </>
  )
}
